import { cn } from '@brew/ui/cn'
import type { ReactNode } from 'react'
import { MarketingContainer } from '@/components/marketing/v2/marketing-container'
import { MARKETING_GUTTER_X } from '@/components/marketing/v2/marketing-layout'

export type IntegrationSurface = 'app' | 'public'

/**
 * The page frame for `/integrations/[provider]` and its public mirror
 * `/browse/integrations/[provider]`.
 *
 * Both the real page and `IntegrationDetailSkeleton` render THIS, so the
 * loading shell cannot describe a different column than the page it precedes.
 * They used to hand-mirror it and drifted on both surfaces: the skeleton was
 * 8px (24px at `lg`) short at the top and 32px short at the bottom on the app
 * surface, and on the public surface it painted a `max-w-5xl` column where
 * the page renders `MarketingContainer`'s default `max-w-7xl` — so the
 * content re-wrapped and changed height as well as sliding sideways.
 *
 * Server-safe, so `loading.tsx` renders it directly.
 */
export function IntegrationDetailFrame({
  surface,
  children,
  ...rest
}: {
  surface: IntegrationSurface
  children: ReactNode
} & React.HTMLAttributes<HTMLDivElement>) {
  if (surface === 'public') {
    return (
      <div
        data-integration-surface
        data-integration-surface-public=""
        className={cn('w-full bg-transparent', MARKETING_GUTTER_X)}
        {...rest}
      >
        <MarketingContainer className="pt-4 pb-16">
          {children}
        </MarketingContainer>
      </div>
    )
  }

  return (
    <div data-integration-surface className="min-h-svh bg-background" {...rest}>
      <div className="mx-auto w-full max-w-5xl px-6 pt-10 pb-16 sm:px-8 lg:pt-14">
        {children}
      </div>
    </div>
  )
}
